import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase";
import { ref, onValue, update } from "firebase/database";
import { Search, Bell, BookOpen, LogOut, ChevronDown, X, Heart, User, ShieldCheck, Tag, TrendingDown, MessageCircle, Moon, Sun } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

function notifIcon(type) {
  if (type === "offer") return <Tag size={14} className="text-blue-500" />;
  if (type === "price_drop") return <TrendingDown size={14} className="text-green-500" />;
  if (type === "sold") return <ShieldCheck size={14} className="text-purple-500" />;
  if (type === "message") return <MessageCircle size={14} className="text-orange-500" />;
  return <Bell size={14} className="text-gray-400" />;
}

export default function Navbar() {
  const { currentUser, userProfile, logout } = useAuth();
  const { dark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const menuRef = useRef(null);
  const notifRef = useRef(null);

  useEffect(() => {
    if (!currentUser) return;
    const notifRef2 = ref(db, `notifications/${currentUser.uid}`);
    const unsub = onValue(notifRef2, (snap) => {
      if (snap.exists()) {
        const items = Object.entries(snap.val()).map(([id, data]) => ({ id, ...data }));
        items.sort((a, b) => b.createdAt - a.createdAt);
        setNotifications(items);
      } else {
        setNotifications([]);
      }
    });
    return unsub;
  }, [currentUser]);

  const unreadCount = notifications.filter(n => !n.read).length;

  async function markAllRead() {
    if (!currentUser || unreadCount === 0) return;
    const updates = {};
    notifications.filter(n => !n.read).forEach(n => {
      updates[`notifications/${currentUser.uid}/${n.id}/read`] = true;
    });
    await update(ref(db), updates);
  }

  function toggleNotif() {
    setNotifOpen(prev => !prev);
    setMenuOpen(false);
    if (!notifOpen) markAllRead();
  }

  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  async function handleLogout() {
    setMenuOpen(false);
    await logout();
    navigate("/login");
  }

  if (!currentUser) return null;

  const displayName = userProfile?.name || currentUser?.displayName || "User";
  const initial = displayName.charAt(0).toUpperCase();

  return (
    <header className="sticky top-0 z-50 max-w-lg mx-auto w-full" style={{ position: "sticky" }}>
      <div className="bg-gradient-to-r from-blue-700 via-blue-600 to-indigo-600 text-white px-4 py-3 flex items-center justify-between shadow-lg">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2">
          <img src="/logo192.png" alt="CampusSwap" className="w-8 h-8 rounded-lg object-contain bg-white p-0.5" />
          <span className="text-lg font-extrabold tracking-tight">
            Campus<span className="text-yellow-300">Swap</span>
          </span>
        </Link>

        <div className="flex items-center gap-1">
          {/* Dark mode toggle */}
          <button onClick={toggleTheme} className="p-2 hover:bg-white/15 rounded-xl transition" title={dark ? "Light mode" : "Dark mode"}>
            {dark ? <Sun size={18} /> : <Moon size={18} />}
          </button>

          {/* Search */}
          <Link to="/browse" className="p-2 hover:bg-white/15 rounded-xl transition">
            <Search size={19} />
          </Link>

          {/* Bell */}
          <div className="relative" ref={notifRef}>
            <button
              onClick={toggleNotif}
              className="p-2 hover:bg-white/15 rounded-xl transition relative"
            >
              <Bell size={19} />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-xs font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-0.5 leading-none badge-pulse">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </button>

            {notifOpen && (
              <div className="absolute top-12 bg-white text-gray-800 rounded-2xl shadow-2xl z-50 w-80 border border-gray-100 overflow-hidden fade-in" style={{ right: '-60px' }}>
                <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between bg-gray-50">
                  <div>
                    <p className="font-bold text-sm text-gray-900">Notifications</p>
                    {unreadCount > 0 && (
                      <p className="text-xs text-blue-600">{unreadCount} unread</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Link
                      to="/notifications"
                      onClick={() => setNotifOpen(false)}
                      className="text-xs text-blue-600 font-semibold hover:underline"
                    >
                      See all
                    </Link>
                    <button onClick={() => setNotifOpen(false)} className="p-1 hover:bg-gray-200 rounded-lg">
                      <X size={14} className="text-gray-500" />
                    </button>
                  </div>
                </div>
                {notifications.length === 0 ? (
                  <div className="px-4 py-8 text-center">
                    <Bell size={32} className="mx-auto mb-2 text-gray-200" />
                    <p className="text-sm text-gray-400 font-medium">No notifications yet</p>
                    <p className="text-xs text-gray-300 mt-0.5">We'll notify you of offers & chats</p>
                  </div>
                ) : (
                  <div className="max-h-72 overflow-y-auto divide-y divide-gray-50">
                    {notifications.slice(0, 20).map(n => (
                      <Link
                        key={n.id}
                        to={n.listingId ? `/listing/${n.listingId}` : "#"}
                        onClick={() => setNotifOpen(false)}
                        className={`flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition ${!n.read ? "bg-blue-50" : ""}`}
                      >
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${!n.read ? "bg-blue-100" : "bg-gray-100"}`}>
                          {notifIcon(n.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-gray-800 font-medium leading-snug">{n.message}</p>
                          <p className="text-xs text-gray-400 mt-0.5">
                            {new Date(n.createdAt).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                          </p>
                        </div>
                        {!n.read && <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1.5" />}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Avatar + Menu */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => { setMenuOpen(!menuOpen); setNotifOpen(false); }}
              className="flex items-center gap-1 hover:bg-white/15 rounded-xl p-1 transition"
            >
              <div className="w-8 h-8 rounded-xl overflow-hidden flex items-center justify-center font-bold text-sm shadow-sm bg-gradient-to-br from-yellow-400 to-orange-400 text-white">
                {userProfile?.photoURL
                  ? <img src={userProfile.photoURL} alt="" className="w-full h-full object-cover" />
                  : initial}
              </div>
              <ChevronDown size={13} className={`transition-transform ${menuOpen ? "rotate-180" : ""}`} />
            </button>

            {menuOpen && (
              <div className="absolute right-0 top-12 bg-white text-gray-800 rounded-2xl shadow-2xl z-50 w-56 border border-gray-100 overflow-hidden fade-in">
                {/* Profile Header */}
                <div className="px-4 py-3 bg-gradient-to-br from-blue-600 to-indigo-600 text-white">
                  <div className="flex items-center gap-2.5">
                    <div className="w-10 h-10 rounded-xl overflow-hidden flex items-center justify-center font-bold text-base shadow-sm flex-shrink-0 bg-gradient-to-br from-yellow-400 to-orange-400 text-white">
                      {userProfile?.photoURL
                        ? <img src={userProfile.photoURL} alt="" className="w-full h-full object-cover" />
                        : initial}
                    </div>
                    <div className="min-w-0">
                      <p className="font-bold text-sm line-clamp-1">{displayName}</p>
                      <p className="text-xs text-blue-200 line-clamp-1">{userProfile?.college || "Student"}</p>
                      {userProfile?.verified && (
                        <div className="flex items-center gap-1 mt-0.5">
                          <ShieldCheck size={10} className="text-green-300" />
                          <span className="text-xs text-green-300">Verified</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="py-1.5">
                  <Link
                    to="/profile"
                    className="flex items-center gap-2.5 px-4 py-2.5 hover:bg-gray-50 text-sm font-medium text-gray-700"
                    onClick={() => setMenuOpen(false)}
                  >
                    <div className="w-7 h-7 bg-blue-50 rounded-lg flex items-center justify-center">
                      <User size={14} className="text-blue-600" />
                    </div>
                    My Profile
                  </Link>
                  <Link
                    to="/my-listings"
                    className="flex items-center gap-2.5 px-4 py-2.5 hover:bg-gray-50 text-sm font-medium text-gray-700"
                    onClick={() => setMenuOpen(false)}
                  >
                    <div className="w-7 h-7 bg-purple-50 rounded-lg flex items-center justify-center">
                      <BookOpen size={14} className="text-purple-600" />
                    </div>
                    My Listings
                  </Link>
                  <Link
                    to="/saved"
                    className="flex items-center gap-2.5 px-4 py-2.5 hover:bg-gray-50 text-sm font-medium text-gray-700"
                    onClick={() => setMenuOpen(false)}
                  >
                    <div className="w-7 h-7 bg-red-50 rounded-lg flex items-center justify-center">
                      <Heart size={14} className="text-red-500" />
                    </div>
                    Saved Items
                  </Link>
                  <div className="border-t border-gray-100 mt-1 pt-1">
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-2.5 px-4 py-2.5 hover:bg-red-50 text-sm font-medium text-red-500 w-full text-left"
                    >
                      <div className="w-7 h-7 bg-red-50 rounded-lg flex items-center justify-center">
                        <LogOut size={14} className="text-red-500" />
                      </div>
                      Logout
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
